import React, { useRef, useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import GavelCircle from 'components/ProMembershipLanding/GavelCircle';
import TierCard from 'components/ProMembershipLanding/TierCard';
import Carousel from 'components/partials/Carousel/Carousel';
import Image from 'next/image';
import useWindowSize from 'hooks/useWindowSize';
import Faq from 'components/ProMembershipLanding/Faq';
import Head from 'next/head';
import { setDashboardPage } from '../../actions';
import MembershipComparison from 'components/ProMembershipLanding/ComparisonTable';
import { membershipData } from 'components/ProMembershipLanding/ComparisonTable/membershipData';
import ManageMembershipModal from 'components/ManageMembershipModal';
import { loginLink } from 'helpers/page';
import { useRouter } from 'next/router';
import { useGlobalContext } from 'context/GlobalContext';
import { isAuctionBuyer } from 'helpers/users';
import './styles.scss';

const gavelWhite = '/static/images/pro-bidder-membership/GavelWhite.png';
const gavelOrange = '/static/images/pro-bidder-membership/GavelOrange.png';
const gavelGrey = '/static/images/pro-bidder-membership/GavelGrey.png';
const gavelYellow = '/static/images/pro-bidder-membership/GavelYellow.png';

interface Testimonial {
    text: React.ReactNode;
    name: string;
    image: string;
}

interface Question {
    inquiry: string;
    answer: React.ReactNode;
}

const ProBidder = () => {
    const globalContext = useGlobalContext();
    const user = globalContext.user;
    const windowSize = useWindowSize();
    const userId = user?.user_id;
    const [showManageMembershipModal, setShowManageMembershipModal] = useState<boolean>(false);
    const router = useRouter();

    useEffect(() => {
        if (userId) {
            globalContext.getUser();
        }
    }, [userId]);

    const testimonials: Testimonial[] = [
        {
            text: (
                <>
                    The greatest value I see in upgrading to the ProBusiness is the greater visibility of units across the country, the ability to have all of my employees and relatives (up to 5) as authorized pickup people in case of emergency, and the additional savings I receive!
                </>
            ),
            name: 'Ryan S.',
            image: '/static/images/pro-bidder-membership/Ryan_S.svg',
        },
        {
            text: (
                <>
                    After calculating what I spent on auctions last year, switching to Pro was a no brainer for me. I spent over $23k so with the 1% discount on my buyer premium and having unlimited bid radius, max bid limit and high bidder limit this just made sense. If I take that 1% discount I would have saved $232 which offsets the annual membership by $128. I have no plans on decreasing the amount of money I spend on auctions, so this was a simple decision for me moving forward for 2024 and years to come.
                </>
            ),
            name: 'Jackie D.',
            image: '/static/images/pro-bidder-membership/Jackie_D.svg',
        },
        {
            text: (
                <>
                    I am a ProBidder on StorageTreasures.com because it saves me money. But,
                    they're always working to make the platform better for us honest bidders and stop the cheats. Thanks for making it easy to do the right thing!
                </>
            ),
            name: 'Eric G.',
            image: '/static/images/pro-bidder-membership/Eric_G.svg',
        },
    ];

    const compareSectionRef = useRef<HTMLDivElement>(null);

    const ScrollToCompareTableLink = () => (
        <a onClick={() => compareSectionRef?.current?.scrollIntoView({behavior: 'smooth'})}>Pro Memberships</a>
    );

    const questions: Question[] = [
        {
            inquiry: 'How does it work?',
            answer:
                <>
                    <p>
                        Upgrading to one of our Pro Memberships is seamless if you're already part of our vibrant bidder community. Each membership level offers you a range of benefits, whether you're a newcomer to the storage unit auction scene or a seasoned pro bidder.
                    </p>
                    <p>Explore our <ScrollToCompareTableLink /> table to see the unique advantages each Pro Membership offers.</p>
                </>,
        },
        {
            inquiry: 'How do I upgrade?',
            answer:
                <>
                    <p>Unlock exclusive benefits by upgrading to a Pro Membership before you bid and win.</p>
                    <ul>
                        <li>Navigate to 'My Account'</li>
                        <li>Select 'Settings'</li>
                        <li>Choose 'Account Settings'</li>
                        <li>Click on 'Manage Membership' </li>
                        <li>Select your desired Pro Membership</li>
                        <li>Confirm by clicking 'Update'</li>
                        <li>Finalize by clicking 'Update' on the confirmation window</li>
                    </ul>
                </>,
        },
        {
            inquiry: 'Which Pro Membership should I choose?',
            answer:
                <>
                    <p>
                        Selecting the appropriate membership is essential to fully leverage your benefits. Our Pro Memberships offer four unique levels, each tailored to cater to a diverse range of needs and buyer preferences. Review our <ScrollToCompareTableLink /> table for a detailed breakdown of the perks and features of each unique membership to see which one best aligns with your requirements and goals.
                    </p>
                </>,
        },
        {
            inquiry: 'What is a bid radius?',
            answer:
                <>
                    <p>
                        A feature that sets a geographical limit for auctions you can bid on based on your zip code. The geographical limit depends on the designated mile radiuses set by your Pro Membership level. Basic membership bidders can bid on auctions within a 300-mile radius of their zip code. Upgrade to one of our paid Pro Memberships and unlock the ability to bid beyond the 300-mile limit. See our <ScrollToCompareTableLink /> table for which radiuses correspond to the different memberships.
                    </p>
                </>,
        },
        {
            inquiry: 'What is a high bidder limit?',
            answer:
                <>
                    <p>
                        This feature limits the number of simultaneous high bids you can place, promoting careful bidding and protecting new bidders. Buyers can be the highest bidder on up to 3 auctions (Basic Bidder) or up to 5 (ProBidder), unless they have completed 5 auctions (Basic Bidder) and 3 auctions (ProBidder). Upgrade to ProBidder Max or ProBusiness for more flexibility and higher bid limits. Check our <ScrollToCompareTableLink /> table for details.
                    </p>
                </>,
        },
/*        {
            inquiry: 'What is a max bid limit?',
            answer:
                <>
                    <p>
                        Buyers cannot bid over $1,000 unless they have completed 5 auctions (Basic members) or 3 auctions (ProBidder members). This limit ensures careful bidding and platform familiarity. Upgrade to ProBidder Max or ProBusiness to remove this restriction. The max bid limit applies retroactively based on completed auctions.
                    </p>
                </>,
        },*/
        {
            inquiry: 'What are the requirements to be an Authorized Pickup Person?',
            answer:
                <>
                    <p>
                        To be an Authorized Pickup Person, you must have a StorageTreasures auction buyer account. You can be an Authorized Pickup Person for as many auction buyers as you like; there are no restrictions.
                    </p>
                </>,
        },
    ];

    const secondQuestions: Question[] = [
        {
            inquiry: 'What are the benefits of ProBidder?',
            answer:
                <>
                    <p>With a ProBidder subscription, buyers enjoy an array of exclusive benefits tailored to enhance their bidding experience:</p>
                    <ul>
                        <li>
                            <strong>15% Buyer Premium: </strong>
                            Enjoy a competitive buyer premium rate.
                        </li>
                        <li>
                            <strong>500mi Bid Radius: </strong>
                            Expand your bidding reach with a wide radius.
                        </li>
                        <li>
                            <strong>High Bidder Limit on 5 Auctions: </strong>
                            Lead the bidding on up to 5 auctions simultaneously. Restriction removed under certain circumstances.
                        </li>
{/*                        <li>
                            <strong>Unlimited Max Bid Amounts after 3 Completed Auctions: </strong>
                            Unlock the ability to bid higher amounts after successfully completing 3 auctions.
                        </li>*/}
                        <li>
                            <strong>1 Authorized Pick-up Person: </strong>
                            Designate someone to pick up your winnings on your behalf.
                        </li>
                        <li>
                            <strong>Dedicated Email & Phone Support: </strong>
                            Access priority support for any inquiries or assistance.
                        </li>
                        <li>
                            <strong>Backup Bidder Offers: </strong>
                            Gain eligibility for backup bid offers, maximizing your chances of winning.
                        </li>
                        <li>
                            <strong>Suspension Review: </strong>
                            Enjoy the opportunity for reinstatement, subject to the severity and number of incidents.
                        </li>
                    </ul>
                </>,
        },
        {
            inquiry: 'What are the benefits of ProBidder Max?',
            answer:
                <>
                    <p>
                        Elevate your bidding experience with ProBidder Max and unlock an exclusive array of advantages:
                    </p>
                    <ul>
                        <li>
                            <strong>12% Buyer Premium: </strong>
                            Enjoy a reduced buyer premium rate, saving you more on your winning bids.
                        </li>
                        <li>
                            <strong>1000mi Bid Radius: </strong>
                            Expand your bidding reach even further with a generous bid radius spanning 1000 miles.
                        </li>
                        <li>
                            <strong>Unlimited High Bidder Opportunities: </strong>
                            Lead the bidding on unlimited number of auctions simultaneously, maximizing your chances of securing desired items.
                        </li>
                        <li>
                            <strong>3 Authorized Pick-up Persons: </strong>
                            Designate up to three individuals to pick up your winnings on your behalf, offering added convenience and flexibility.
                        </li>
                        <li>
                            <strong>Dedicated Email & Phone Support: </strong>
                            Access priority support via email and phone channels for prompt assistance with any queries or concerns.
                        </li>
                        <li>
                            <strong>Backup Bidder Offers: </strong>
                            Increase your winning opportunities with eligibility for backup bid offers, ensuring you don't miss out on coveted items.
                        </li>
                        <li>
                            <strong>Suspension Review: </strong>
                            Enjoy the peace of mind of reinstatement eligibility, subject to the severity and number of incidents.
                        </li>
                    </ul>
                </>,
        },
        {
            inquiry: 'What are the benefits of ProBusiness?',
            answer:
                <>
                    <p>
                        Step into the realm of elite auction buying with ProBusiness, our highest level meticulously crafted for auction buyer professionals seeking unparalleled advantages. Here's a glimpse into the exclusive benefits awaiting these elite bidders:
                    </p>
                    <ul>
                        <li>
                            <strong>9% Buyer Premium: </strong>
                            Enjoy an unmatched buyer premium rate, optimizing your savings on winning bids.
                        </li>
                        <li>
                            <strong>Unlimited Bid Radius: </strong>
                            Expand your bidding horizons limitlessly, ensuring you never miss out on coveted auctions, regardless of location.
                        </li>
                        <li>
                            <strong>Unlimited High Bidder Opportunities: </strong>
                            Lead the bidding on unlimited number of auctions simultaneously, maximizing your chances of securing desired items.
                        </li>
                        <li>
                            <strong>5 Authorized Pick-up Persons: </strong>
                            Designate up to five individuals to collect your winnings on your behalf, providing unparalleled convenience and flexibility.
                        </li>
                        <li>
                            <strong>Dedicated Email & Phone Support: </strong>
                            Access priority support channels for swift resolution of any queries or concerns, ensuring a seamless buying experience.
                        </li>
                        <li>
                            <strong>Backup Bidder Offers: </strong>
                            Increase your chances of winning with eligibility for backup bid offers, ensuring you never miss out on valuable opportunities.
                        </li>
                        <li>
                            <strong>Suspension Review: </strong>
                            Enjoy the assurance of reinstatement eligibility, subject to the severity and number of incidents.
                        </li>
                    </ul>
                </>,
        },
        {
            inquiry: 'How do I get backup bid offers?',
            answer:
                <>
                    <p>
                        Backup bidder offers are extended when the original winning bidder fails to complete the unit clean-out process. As an active bidder on our platform, you become eligible for these offers by enrolling in one of our Pro Memberships. By upgrading your membership, you unlock the opportunity to participate in backup bid offers, providing you with additional chances to secure units and expand your auction success. Please note that Basic membership holders are eligible for backup bid offers after having 5 won auctions.
                    </p>
                </>,
        },
        {
            inquiry: 'What is a buyer premium?',
            answer:
                <>
                    <p>
                        The buyer premium is a fee charged by StorageTreasures to your credit card on file, upon winning a storage auction. This fee covers the utilization of our services and is calculated by multiplying the buyer premium percentage by the final bid price. Your specific buyer premium percentage is determined by the type of membership you hold. There is a minimum buyer premium of $10. Additionally, there may be a purchase deposit amount (up to 10% of the sale price) charged to your card, which is deducted from the 'Balance Due at facility' amount. Buyer premiums range from 18% to 9% depending on your membership level.
                    </p>
                </>,
        },
        {
            inquiry: 'What are the requirements to have an Authorized Pickup Person?',
            answer:
                <>
                    <p>
                        To have an Authorized Pickup Person, a user must have a StorageTreasures auction buyer account, with a Pro membership. The number of available Authorized Pickup Person depends on the user's membership.
                    </p>
                </>,
        },
    ];

    const handleMembershipClick = () => {
        if (user?.authed) {
            setShowManageMembershipModal(true);
        } else {
            router.push(loginLink({ redirect_to: '/pro-membership-upgrade' }));
        }
    };

    const allQuestions: Question[] = [...questions, ...secondQuestions];

    const isDisabled = user?.authed && !isAuctionBuyer(user?.reg_type);

    return (
        <div className="pro-membership-main-container">
            <Head>
                <title>Pro Memberships | StorageTreasures</title>
                <meta name="description" content="Upgrade to our new pro membership today to elevate your bidding experience on StorageTreasures.com" />
                <meta name="keywords" content="pro membership, pro membership upgrade" />
            </Head>
            <div className="membership-upgrade-title-box">
                <h1>Pro Memberships</h1>
                <div className="gavel-container">
                    <GavelCircle
                        image={gavelWhite}
                        text="Basic Bidder"
                        color="rgba(255, 255, 255, 0.85)"
                    />
                    <GavelCircle
                        image={gavelOrange}
                        text="ProBidder"
                        color="rgba(205, 127, 50, 0.85)"
                    />
                    <GavelCircle
                        image={gavelGrey}
                        text="ProBidder Max"
                        color="rgba(192, 192, 192, 0.85)"
                    />
                    <GavelCircle
                        image={gavelYellow}
                        text="ProBusiness"
                        color="rgba(206, 169, 54, 0.85)"
                    />
                </div>
            </div>
            <Row className="d-flex align-items-center justify-content-center memberships-description">
                <h2>Upgrade to Pro Today!</h2>
                <p className="text-center">
                    Introducing our new Pro Memberships, designed to elevate your bidding experience to new heights. Unlock extra savings with
                    reduced bidder premiums, gain unlimited access to bid on auctions without limitations of distance or winning thresholds,
                    and enjoy priority customer support to address any inquiries or assistance you may need during the bidding process.
                </p>
            </Row>
            <Row className="d-flex align-items-center justify-content-center">
                <h2 className="tier-cards-title">Pro Memberships</h2>
            </Row>
            <Row className="d-flex align-items-center justify-content-center m-2 mx-5 mx-xl-0">
                <Col className="tier-card-col" md={12} lg={{ span: 3}} xl={2}>
                    <TierCard
                        color="#FFFFFF"
                        text="Basic Bidder"
                        image={gavelWhite}
                        description="Kickstart your bidding journey with our free account. Bid and win on local auctions with ease."
                        bullets={['18% Buyer Premium', '300 Mile Bid Radius', 'No Authorized Pick-Up Person', 'No Dedicated Customer Service']}
                        btnLabel="$0 / Month"
                        btnClassName="primary"
                        disabled={isDisabled}
                        onClick={handleMembershipClick}
                    />
                </Col>
                <Col className="tier-card-col " md={12} lg={{ span: 3}} xl={2}>
                    <TierCard
                        color="#CD7F32"
                        text="ProBidder"
                        image={gavelOrange}
                        description="Enhance your bidding benefits and save! Gain access to a range of pro features."
                        bullets={['15% Buyer Premium', '500 Mile Bid Radius', '1 Authorized Pick-Up Person', 'Dedicated Customer Service']}
                        btnLabel="$10 / Month"
                        btnClassName="primary"
                        disabled={isDisabled}
                        onClick={handleMembershipClick}
                    />
                </Col>
                <Col className="tier-card-col " md={12} lg={{ span: 3}} xl={2}>
                    <TierCard
                        color="#C0C0C0"
                        text="ProBidder Max"
                        image={gavelGrey}
                        description="Maximize your benefits and save even more with exclusive access and priority support."
                        bullets={['12% Buyer Premium', '1000 Mile Bid Radius', '3 Authorized Pick-Up Person', 'Dedicated Customer Service']}
                        btnLabel="$20 / Month"
                        btnClassName="primary"
                        disabled={isDisabled}
                        onClick={handleMembershipClick}
                    />
                </Col>
                <Col className="tier-card-col" md={12} lg={{ span: 3}} xl={2}>
                    <TierCard
                        color="#CEA936"
                        text="ProBusiness"
                        image={gavelYellow}
                        description="Elevate your bidding prowess with unparalleled saving and experience the pinnacle of bidding and winning!"
                        bullets={['9% Buyer Premium', 'Unlimited Bid Radius', '5 Authorized Pick-Up Person', 'Dedicated Customer Service']}
                        btnLabel="$30 / Month"
                        btnClassName="primary"
                        disabled={isDisabled}
                        onClick={handleMembershipClick}
                    />
                </Col>
            </Row>
            <Row className="d-flex align-items-center justify-content-center">
                <h2 className="tier-cards-title">Testimonials</h2>
            </Row>
            <Row className="carousel-row">
                <Carousel
                    name="pro-membership-testimonials"
                    accentColor="primary"
                    timeout="7000"
                    showIndicators={false}
                    controlsColor="black"
                    minHeight="120"
                    className="carousel-max-content"
                >
                    {testimonials.map((item, idx) => {
                        return (
                            <div key={idx}>
                                <div className="testimonial-container">
                                    <Image src={item.image} width={100} height={100} />
                                    <div className="testimonial-data">
                                        <p>{item.text}</p>
                                        <p className="testimonial-name">-{item.name}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </Carousel>
            </Row>
            <div ref={compareSectionRef}>
                <Row className="d-flex align-items-center justify-content-center compare-container">
                    <h2 className="tier-cards-title">Compare Pro Memberships</h2>
                    <p className="tier-cards-content">
                        Whether you’re a beginner looking to dip your toes into the world of bidding or a seasoned professional seeking advanced perks, our Pro
                        Bidder Memberships are built for every level. Compare the various membership levels below to see which membership aligns with your
                        bidding goals and preferences.
                    </p>
                </Row>
            </div>

            <MembershipComparison
                memberships={membershipData}
                authed={user?.authed}
                onClick={handleMembershipClick}
                disableBtns={isDisabled}
            />

            <Row className="d-flex align-items-center justify-content-center faq-title">
                <Col>
                    <h2 className="tier-cards-title">FAQ</h2>
                    <p className="tier-cards-content text-center">
                        We have answers to the most frequently asked questions around our Pro Memberships. Learn more about the ins and outs of our
                        membership tiers, benefits, and how to make the most out of your bidding experience.
                    </p>
                </Col>
            </Row>
            <Row className="mb-5">
                {windowSize.width > 991 ? (
                    <>
                        <Col md={6}>
                            <Faq questions={questions} />
                        </Col>
                        <Col md={6}>
                            <Faq questions={secondQuestions} />
                        </Col>
                    </>
                ) : (
                    <Col md={12}>
                        <Faq questions={allQuestions} />
                    </Col>
                )}
            </Row>
            <ManageMembershipModal
                membership_type={user?.membership?.type}
                show={showManageMembershipModal}
                onHide={() => setShowManageMembershipModal(false)}
                user_id={userId}
                payment_method={user?.payment_method}
                handleMembershipSubmitSuccess={() => globalContext.getUser()}
            />
        </div>
    );
};

ProBidder.getInitialProps = async ({ store: { dispatch } }) => {
    await Promise.all([
        dispatch(setDashboardPage({ title: false, menuEnum: false, pageAction: false })),
    ]);

    const pageData =  { title: undefined, h1: undefined, description: undefined, marginBottom: '' };

    return { pageData };
};

export default ProBidder;
